import { yupResolver } from '@hookform/resolvers/yup';
import { Button, InputAdornment, Stack } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import React from 'react';
import { SubmitHandler, useForm } from 'react-hook-form';
import {
	RadioButtonGroup,
	SelectElement,
	TextFieldElement,
} from 'react-hook-form-mui';
import * as Yup from 'yup';
import {
	DEPRECATED__CLAIMS_ONLY_USER__ACCOUNT_TYPE,
	DEPRECATED__EMPLOYEE__ACCOUNT_TYPE,
	DEPRECATED__READ_ONLY_USER__ACCOUNT_TYPE,
	INTERNAL_ADMIN__ACCOUNT_TYPE,
	ROLES_AND_PERMISSION__ACCOUNT_TYPE,
} from '../../../../context/auth';
import UserContext from '../../../../context/userContext';
import { FORM_MESSAGE } from '../../../fastForm/data/constants';
import { RolesApi, TenantsApi } from '../api';
import {
	CLIENT_FORM__CANCEL_BUTTON,
	CLIENT_FORM__CLIENT_ID_LABEL,
	CLIENT_FORM__CLIENT_TYPE_LABEL,
	CLIENT_FORM__CREATE_CLIENT_BUTTON,
	CLIENT_FORM__ROLE_LABEL,
	CLIENT_FORM__TENANT_TYPE__LEGACY_TENANT_LABEL,
	CLIENT_FORM__TENANT_TYPE__NAMESPACE_LABEL,
	CLIENT_FORM__TENANT_TYPE__TENANT_LABEL,
	INTERNAL_ADMIN_LABEL,
	LEGACY_AUTH_TENANT_CLAIM_ONLY_LABEL,
	LEGACY_AUTH_TENANT_EMPLOYEE_LABEL,
	LEGACY_AUTH_TENANT_READ_ONLY_LABEL,
	USE_ROLE_ACCOUNT_TYPE,
	USE_ROLE_ACCOUNT_TYPE_LABEL,
} from '../data/constants';
import { ClientRequest } from '../types';

interface SelectOption {
	id: string;
	label: string;
}

interface DefaultOptions {
	tenant: {
		[tenantName: string]: SelectOption;
	};
	legacy_tenant: {
		[tenantName: string]: SelectOption;
	};
	namespace: {
		[namespace: string]: SelectOption;
	};
}

const getSchema = (namespaceLength: number) =>
	Yup.object().shape({
		tenant: Yup.string().required(FORM_MESSAGE.required),
		clientId: Yup.string()
			.required(FORM_MESSAGE.required)
			.max(31 - namespaceLength, FORM_MESSAGE.thirtyTwoCharacterLimit)
			.test(
				'no-special-characters',
				FORM_MESSAGE.noSpecialCharactersOrWhitespace,
				(value) => {
					if (value) {
						const regex = /^[a-zA-Z0-9_\-~]+$/;
						return regex.test(value);
					}
					return true;
				},
			),
		role: Yup.string().when('clientType', {
			is: 'legacy_tenant',
			then: Yup.string().required(FORM_MESSAGE.required),
		}),
	});
interface Props {
	initialValues: ClientRequest;
	handleSubmit: SubmitHandler<ClientRequest>;
	handleClose: () => void;
	isLoading: boolean;
}

interface ClientForm extends ClientRequest {
	clientType: 'tenant' | 'legacy_tenant' | 'namespace';
}

const ClientForm = (props: Props) => {
	const { initialValues, handleSubmit, isLoading, handleClose } = props;
	const { selectedUserRole: user } = React.useContext(UserContext);
	const ROLES_KEY = RolesApi.ROLES_KEY + '-' + user.namespace;
	const TENANTS_KEY = TenantsApi.TENANTS_KEY + '-' + user.namespace;

	const { data: availableRoles } = useQuery({
		queryKey: [ROLES_KEY],
		queryFn: RolesApi.getRoles,
	});

	const { data: tenants } = useQuery({
		queryKey: [TENANTS_KEY],
		queryFn: TenantsApi.getTenants,
	});

	const {
		control,
		watch,
		setValue,
		handleSubmit: useFormSubmit,
	} = useForm<ClientForm>({
		defaultValues: {
			...initialValues,
			clientType: 'tenant',
		},
		resolver: yupResolver(getSchema(user.namespace.length)),
		reValidateMode: 'onChange',
	});

	const clientType = watch('clientType');

	React.useEffect(() => {
		setValue('tenant', '');
		setValue('role', '');
	}, [clientType, setValue]);

	const onSubmit: SubmitHandler<ClientForm> = async (values) => {
		let role = '';
		if (clientType === 'namespace') {
			role = `#namespace.owner@${user.namespace}`;
		}
		if (clientType === 'tenant') {
			role = `$${USE_ROLE_ACCOUNT_TYPE}@${values.tenant}`;
		}
		if (clientType === 'legacy_tenant') {
			role = `$${values.role}@${values.tenant}`;
		}
		handleSubmit({
			clientId: user.namespace + '.' + values.clientId,
			role,
		});
	};

	const defaultOptions: DefaultOptions = {
		tenant: {},
		legacy_tenant: {},
		namespace: {},
	};

	const defaultRoles = {
		tenant: [
			{
				id: USE_ROLE_ACCOUNT_TYPE,
				label: USE_ROLE_ACCOUNT_TYPE_LABEL,
			},
		],
		legacy_tenant: [
			{
				id: DEPRECATED__CLAIMS_ONLY_USER__ACCOUNT_TYPE,
				label: LEGACY_AUTH_TENANT_CLAIM_ONLY_LABEL,
			},
			{
				id: DEPRECATED__READ_ONLY_USER__ACCOUNT_TYPE,
				label: LEGACY_AUTH_TENANT_READ_ONLY_LABEL,
			},
			{
				id: DEPRECATED__EMPLOYEE__ACCOUNT_TYPE,
				label: LEGACY_AUTH_TENANT_EMPLOYEE_LABEL,
			},
		],
		namespace: [
			{
				id: INTERNAL_ADMIN__ACCOUNT_TYPE,
				label: INTERNAL_ADMIN_LABEL,
			},
		],
	};

	if (tenants && availableRoles) {
		availableRoles?.forEach((role) => {
			const tenant = tenants.find(
				(tenant) => tenant.hostname === role.tenantHostname,
			);

			switch (role.accountType) {
				case ROLES_AND_PERMISSION__ACCOUNT_TYPE:
					if (tenant?.useRolesAndPermissions) {
						if (!defaultOptions.tenant[role.tenantHostname])
							defaultOptions.tenant[role.tenantHostname] = {
								id: role.tenantHostname,
								label: role.tenantHostname,
							};
					}
					break;
				case INTERNAL_ADMIN__ACCOUNT_TYPE:
					if (!defaultOptions.namespace[role.namespace])
						defaultOptions.namespace[role.namespace] = {
							id: role.namespace,
							label: role.namespace,
						};
					break;
				case DEPRECATED__CLAIMS_ONLY_USER__ACCOUNT_TYPE:
				case DEPRECATED__EMPLOYEE__ACCOUNT_TYPE:
				case DEPRECATED__READ_ONLY_USER__ACCOUNT_TYPE:
					if (!tenant?.useRolesAndPermissions) {
						if (!defaultOptions.legacy_tenant[role.tenantHostname]) {
							defaultOptions.legacy_tenant[role.tenantHostname] = {
								id: role.tenantHostname,
								label: role.tenantHostname,
							};
						}
					}
					break;
			}
		});
	}

	return (
		<form onSubmit={useFormSubmit(onSubmit)}>
			<Stack spacing={2} pb={2}>
				<RadioButtonGroup
					label={CLIENT_FORM__CLIENT_TYPE_LABEL}
					name='clientType'
					control={control}
					options={[
						{
							id: 'tenant',
							label: CLIENT_FORM__TENANT_TYPE__TENANT_LABEL,
						},
						{
							id: 'legacy_tenant',
							label: CLIENT_FORM__TENANT_TYPE__LEGACY_TENANT_LABEL,
						},
						{
							id: 'namespace',
							label: CLIENT_FORM__TENANT_TYPE__NAMESPACE_LABEL,
						},
					]}
					row
				/>
				<TextFieldElement
					control={control}
					disabled={isLoading}
					name='clientId'
					variant='outlined'
					fullWidth
					label={CLIENT_FORM__CLIENT_ID_LABEL}
					InputProps={{
						startAdornment: (
							<InputAdornment position='start'>
								{user.namespace}.
							</InputAdornment>
						),
					}}
				/>
				<SelectElement
					control={control}
					name='tenant'
					variant='outlined'
					label={
						clientType === 'namespace'
							? CLIENT_FORM__TENANT_TYPE__NAMESPACE_LABEL
							: clientType === 'legacy_tenant'
							? CLIENT_FORM__TENANT_TYPE__LEGACY_TENANT_LABEL
							: CLIENT_FORM__TENANT_TYPE__TENANT_LABEL
					}
					options={Object.keys(defaultOptions[clientType]).map(
						(key) => defaultOptions[clientType][key],
					)}
				/>
				{clientType === 'legacy_tenant' && (
					<SelectElement
						name='role'
						control={control}
						variant='outlined'
						label={CLIENT_FORM__ROLE_LABEL}
						options={defaultRoles[clientType]}
					/>
				)}
			</Stack>
			<Stack direction='row' spacing={2} mt={2} justifyContent='flex-end'>
				<Button color='inherit' onClick={handleClose} disabled={isLoading}>
					{CLIENT_FORM__CANCEL_BUTTON}
				</Button>
				<Button
					variant='contained'
					color='secondary'
					type='submit'
					disabled={isLoading}
				>
					{CLIENT_FORM__CREATE_CLIENT_BUTTON}
				</Button>
			</Stack>
		</form>
	);
};

export default ClientForm;
